"use client"

import Link from 'next/link'
import { Plus, Printer } from 'lucide-react'
import { Button } from '../../../shared/components/ui/button'
import HomeOwnersTablePagination from './home-owners-table-pagination'
import { HOMEOWNERS_ROUTES } from '../../../shared/constants/pages-routes'
import HomeOwnersListPreloaded from './home-owners-list-preloaded'
import HomeOwnersCardsMobile from './home-owners-cards-mobile'
import HomeOwnersFilterButton from './home-owners-filter-button'
import FilterSearchHomeInputCleanV2 from './filter-search-home-input-clean-v2'
import { HomeownersQuickWorkscopeStatusFilters } from './homeowners-quick-workscope-status-filters'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '../../../shared/constants/user-roles'
import { useRegionAwareHref } from '@/shared/hooks/use-region-aware-href'
import { useSearchParams } from 'next/navigation'
import { useIsDesktopXL } from '@/shared/hooks/use-mobile'
import { FilterSearchExportProvider } from '../context/filter-search-export-context'
import HomeownersDownloadButton from './homeowners-download-button'
import { OrganizationOptionsProvider } from '../context/organization-options-context'

function FilterSearchTab() {
    const { session } = useSession()
    const createHref = useRegionAwareHref(HOMEOWNERS_ROUTES.create)
    const searchParams = useSearchParams()
    const isDesktopXL = useIsDesktopXL()
    // Key list by workscope/workscopeStatus so when these filters change we remount and fetch with new params
    const workscopeKey = searchParams.get('workscope') ?? ''
    const workscopeStatusKey = searchParams.get('workscopeStatus') ?? ''
    const workscopeGoodwillKey = searchParams.get('workscopeGoodwill') ?? ''
    const workscopeGoodwillStatusKey = searchParams.get('workscopeGoodwillStatus') ?? ''
    const listKey = `w-${workscopeKey}-s-${workscopeStatusKey}-gw-${workscopeGoodwillKey}-gws-${workscopeGoodwillStatusKey}`
    const canPrint = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
    return (
        <>
            <OrganizationOptionsProvider>
                <FilterSearchExportProvider>
                {/* Mobile: no card (no border/rounded); sm+ card style. Tighter bottom margin on mobile. Hidden when printing. */}
                <div className='border-0 rounded-none px-0 py-3 mb-2 sm:border sm:rounded sm:px-3 sm:py-4 sm:mb-4 lg:py-5 lg:mb-6 w-full min-w-0 overflow-hidden print-hidden'>
                    <div className="flex flex-col gap-2 sm:gap-3 w-full min-w-0">
                        {/* Mobile: two rows. Row 1 = Filter + Workscope + Goodwill + Status. Row 2 = Search + Add */}
                        <div className="flex flex-col gap-2 sm:hidden w-full min-w-0">
                            <div className="flex flex-nowrap items-center gap-2 min-w-0 w-full overflow-x-auto">
                                <HomeOwnersFilterButton />
                                <HomeownersQuickWorkscopeStatusFilters show="scopeGoodwill" className="flex flex-nowrap items-center gap-2 min-w-0 shrink" />
                                <HomeownersQuickWorkscopeStatusFilters show="status" className="flex items-center gap-2 shrink-0 min-w-0" />
                            </div>
                            <div className="flex flex-1 min-w-0 gap-2 items-center w-full">
                                <div className="flex-1 min-w-0">
                                    <FilterSearchHomeInputCleanV2 />
                                </div>
                                {(session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER) && (
                                    <Link href={createHref} className="shrink-0">
                                        <Button variant="default" size="sm" className="bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10">
                                            <Plus className='w-5 h-5' />
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        </div>
                        {/* Desktop: single row = Filter + Workscope + Goodwill + Status + Search + Add + Print — hidden when printing */}
                        <div className="hidden sm:flex flex-wrap items-center gap-2 min-w-0 w-full print-hidden">
                            <HomeOwnersFilterButton />
                            <HomeownersQuickWorkscopeStatusFilters show="scopeGoodwill" className="flex flex-wrap items-center gap-2 min-w-0" />
                            <HomeownersQuickWorkscopeStatusFilters show="status" className="flex flex-wrap items-center gap-2" />
                            <div className="flex-1 min-w-[200px] print-hidden">
                                <FilterSearchHomeInputCleanV2 />
                            </div>
                            {(session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER) && (
                                <Link href={createHref} className="shrink-0 print-hidden">
                                    <Button variant="default" size="sm" className="bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10">
                                        <Plus className='w-5 h-5' />
                                        <span>Add</span>
                                    </Button>
                                </Link>
                            )}
                            <HomeownersDownloadButton />
                            {canPrint && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="shrink-0 gap-2 py-1 h-10 print-hidden"
                                    onClick={() => window.print()}
                                    type="button"
                                >
                                    <Printer className="w-4 h-4" />
                                    <span>Print</span>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Desktop table: mount only at xl so one consumer runs the filtered query and receives data */}
                {isDesktopXL && (
                    <div className='border rounded px-3 py-5 lg:space-y-4 space-y-6 mb-10'>
                        <HomeOwnersListPreloaded
                            key={listKey}
                            workscopeParam={workscopeKey}
                            workscopeStatusParam={workscopeStatusKey}
                            mapRoute={HOMEOWNERS_ROUTES.map}
                            hideFlowchartLinks={session?.role !== UserRole.ADMIN}
                        />
                        <HomeOwnersTablePagination />
                    </div>
                )}
                {/* Mobile cards: mount only below xl */}
                    {!isDesktopXL && (
                    <HomeOwnersCardsMobile
                        mapRoute={HOMEOWNERS_ROUTES.map}
                        hideFlowchartLinks={session?.role !== UserRole.ADMIN}
                    />
                )}
                </FilterSearchExportProvider>
            </OrganizationOptionsProvider>
        </>
    )
}

export default FilterSearchTab
